import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Lock } from 'lucide-react'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'

const Login: React.FC = () => {
  // Email authentication (active)
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [showOtpField, setShowOtpField] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(0)

  // Phone authentication (commented for future use)
  // const [phoneNumber, setPhoneNumber] = useState('')

  const { login } = useAuth()

  // Countdown timer effect
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1)
      }, 1000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [countdown])

  // Email authentication (active)
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedEmail = email.trim().toLowerCase()

    if (!trimmedEmail) {
      setError('Please enter an email address')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Send OTP request to backend
      const response = await api.post('/auth/send-email-otp', { email: trimmedEmail })

      if (response.success) {
        setShowOtpField(true)
        setCountdown(45) // Set initial countdown when OTP is sent
        setError('')
      } else if (response.cooldown) {
        setError(`Please wait ${response.cooldown} seconds before requesting another OTP`)
      } else {
        setError('Failed to send OTP. Please try again.')
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send OTP')
    } finally {
      setIsLoading(false)
    }
  }

  // Phone authentication (commented for future use)
  /*
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedPhoneNumber = phoneNumber.trim()

    if (!trimmedPhoneNumber) {
      setError('Please enter a phone number')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Send OTP request to backend
      await api.post('/auth/send-otp', { phoneNumber: `+91${trimmedPhoneNumber}` })
      setShowOtpField(true)
      setError('')
    } catch (error: any) {
      setError(error.message || 'Failed to send OTP')
    } finally {
      setIsLoading(false)
    }
  }
  */

  // Email authentication (active)
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otp.trim()) {
      setError('Please enter the OTP')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const success = await login(email.trim().toLowerCase(), otp)
      if (!success) {
        setError('Invalid OTP or access denied. Only admin users can access this portal.')
      }
    } catch (error) {
      setError('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Phone authentication (commented for future use)
  /*
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otp.trim()) {
      setError('Please enter the OTP')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const success = await login(`+91${phoneNumber.trim()}`, otp)
      if (!success) {
        setError('Invalid OTP or access denied. Only admin users can access this portal.')
      }
    } catch (error) {
      setError('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  */

  // Email authentication (active)
  const handleResendOtp = async () => {
    setIsLoading(true)
    setError('')

    try {
      const response = await api.post('/auth/send-email-otp', { email: email.trim().toLowerCase() })

      if (response.success) {
        setCountdown(45) // Reset countdown
        setOtp('') // Clear current OTP
        setError('')
        // Could show a success message here
      } else if (response.cooldown) {
        setError(`Please wait ${response.cooldown} seconds before requesting another OTP`)
      } else {
        setError('Failed to resend OTP. Please try again.')
      }
    } catch (error: any) {
      setError(error.message || 'Failed to resend OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Phone authentication (commented for future use)
  /*
  const handleResendOtp = async () => {
    setIsLoading(true)
    setError('')

    try {
      await api.post('/auth/send-otp', { phoneNumber: `+91${phoneNumber.trim()}` })
      setError('')
      setOtp('')
      // Could show a success message here
    } catch (error: any) {
      setError(error.message || 'Failed to resend OTP')
    } finally {
      setIsLoading(false)
    }
  }
  */

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-primary rounded-lg flex items-center justify-center">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access the admin dashboard
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow rounded-lg">
          {!showOtpField ? (
            <form className="space-y-6" onSubmit={handleSendOtp}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Phone Number Form (commented for future use)
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1">
                  <div className="flex">
                    <span className="inline-flex items-center px-3 py-2 border border-gray-300 bg-gray-100 text-gray-600 rounded-l-md">+91</span>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      required
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 border-l-0 rounded-r-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Enter your phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
              */}

              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                  {error}x
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={cn(
                    'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
                    isLoading && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label htmlFor="email-display" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email-display"
                    type="email"
                    value={email}
                    disabled
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Phone display (commented for future use)
              <div>
                <label htmlFor="phone-display" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1">
                  <input
                    id="phone-display"
                    type="text"
                    value={phoneNumber}
                    disabled
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 sm:text-sm"
                  />
                </div>
              </div>
              */}

              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  OTP Code
                </label>
                <div className="mt-1">
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={isLoading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Check your email for the verification code
                </p>
              </div>

              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className={cn(
                    'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
                    (isLoading || otp.length !== 6) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Verify & Login'
                  )}
                </button>

                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-sm text-gray-500 mb-2">
                      Resend OTP in {formatCountdown(countdown)}
                    </p>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={isLoading}
                        className="text-sm text-primary hover:text-primary/80 disabled:opacity-50 font-medium"
                      >
                        {isLoading ? 'Sending...' : 'Resend OTP'}
                      </button>
                      <span className="mx-2 text-gray-400">|</span>
                    </>
                  )}
                  {countdown === 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowOtpField(false)
                        setOtp('')
                        setError('')
                        setCountdown(0)
                      }}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Change Email Address
                    </button>
                  )}
                  {countdown > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowOtpField(false)
                        setOtp('')
                        setError('')
                        setCountdown(0)
                      }}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Change Email Address
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Restricted access for administrators only
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login